<template>
  <v-layout>
    <div class="discussion-input">
      <interaction-tinymce
        @tinymce-input="handleTinymceInput"
        :ariaLabel="ariaLabel"
        :inline="true"
        :existing-text="commentInput"
        :tinymce-key="commenterKey"
        :interaction-id="interactionId"
        ref="tinymce"
        :placeholderText="placeholder"
        font-size-class="pp-interaction-title"
        :enableUpload="enableUpload"
        :disabledButtons="disabledButtons"
        @expand-all-reply-threads="expandAllReplyThreads"
        :showReplies="showReplies"        
        :mode="mode"
        v-if="!isLiveSession || (isLiveSession && !interactionMeta.pastSessionTime)"
      ></interaction-tinymce>
    </div>
    <div>
      <v-btn
        v-if="mode === 'comment' && commentInput.length > 0"
        :disabled="commentInput.length === 0 || (isLiveSession && interactionMeta.pastSessionTime)"
        flat
        icon
        small
        :color="branding.activeColor"
        @click="createComment()"
        aria-label='Submit new comment'>
        <v-icon>send</v-icon>
      </v-btn>
      <v-btn
        v-if="mode === 'edit' && commentInput.length > 0"
        :disabled="commentInput.length === 0 || (isLiveSession && interactionMeta.pastSessionTime)"
        flat
        icon
        small
        :color="branding.activeColor"
        @click="updateComment"
        aria-label="Submit updated comment">
        <v-icon>send</v-icon>
      </v-btn>
      <v-btn
        v-if="mode === 'reply' && commentInput.length > 0"
        :disabled="commentInput.length === 0 || (isLiveSession && interactionMeta.pastSessionTime)"
        flat
        icon
        small
        :color="branding.activeColor"
        @click="createComment(commentId)"
        aria-label="Submit comment">
        <v-icon>send</v-icon>
      </v-btn>
      <v-tooltip
        v-if="(
          mode === 'comment'
          && commentInput.length === 0
        )"
        v-model="showTooltip"
        top>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            slot="activator"
            @click="refreshDiscussionComments"
            flat
            icon
            small
            :color="branding.activeColor"
            aria-label="Refresh Discussions"
            class="d-inline-flex ml-0"
            @mouseover="mouseOver"
            @mouseleave="mouseExit"
            :disabled="isLiveSession && interactionMeta.pastSessionTime">
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span @mouseover="mouseOver" @mouseleave="mouseExit">Refresh discussions</span>
      </v-tooltip>
    </div>
  </v-layout>
</template>

<script>
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import XApiService from '@/services/XApiService';
import EventBus from '@/buses/EventBus';
import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment';

import InteractionTinymce from '@/components/interaction/InteractionTinymce';

export default {
  name: 'discussion-input',
  components: {
     InteractionTinymce,
  },
  props: [
    'ariaLabel',
    'commentId', // id of comment to edit or reply to
    'interactionId',
    'startingText', // the text of the input field
    'mode', // 'edit' or 'reply' or 'comment'
    'placeholder',
    'visible',
    'showReplies',
  ],
  data() {
    return {
      commentInput: this.startingText || '',
      enableUpload: true,
      hoverTimer: null,
      showTooltip: false,
    };
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
    ]),
    ...mapGetters('player',[
      'branding',
      'bulb',
      'bulbAttempt',
      'displayTime',
      'playerState',
      'user',
    ]),
    commenterKey() {
      if (this.mode === 'comment') return `discussion-comment-${this.interaction.id}-new`;
      else if (this.mode === 'edit') return `discussion-edit-${this.commentId || 0}`;
      else if (this.mode === 'reply') return `discussion-reply-${this.commentId || 0}`;
      else return `commenter-rouge-${Date()}`;
    },
    discussionPreview() {
      // if anonymous user or teacher without a section use the preview discussions - comments are not saved and other users' comments cannot be viewed
      return this.user.userType === 'anonymous' || (this.user.userType === 'teacher' && !this.bulbAttempt.sectionId);
    },
    disabledButtons() {
      if (_.get(this.interaction, 'config.enable_upload', 0)) return '';
      return ['video'];
    },
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId) || {};
    },
    interactionAttempt() {
      return this.$store.getters['interactions/interactionAttempt'](this.interactionId) || {};
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
  },
  methods: {
    ...mapMutations('player', [
      'setSnackbar',
    ]),
    ...mapMutations('interactions', [
      'updateDummyCommentSubmitted',
      'setOldDiscussionComments',
    ]),
    ...mapActions('interactions', [
      'createDiscussionComment',
      'createDummyDiscussionComment',
      'loadDiscussionComments',
      'updateDiscussionComment',
      'updateDummyDiscussionComment',
    ]),
    createComment(parentId = null) {
      if (this.discussionPreview) {
        this.createDummyComment(parentId)
        this.updateDummyCommentSubmitted(true);
        return;
      }
      this.interactionAttempt.displayTime = this.displayTime;
      const autograde = this.interaction.autograde;
      const clone = _.cloneDeep(this.interactionAttempt);
      // if teacher user, add id to the discussion attempt comment
      clone.teacherId = this.user.userType === 'teacher' ? this.user.id : null;
      clone.sectionId = this.bulbAttempt.sectionId;
      this.createDiscussionComment({interactionAttempt: clone, parentId, autograde})
      .then(created => this.loadDiscussionComments({
          sectionId: this.bulbAttempt.sectionId,
          interactionId: this.interactionId
        })
      ).then(() => {
        this.$refs.tinymce.clear();
        this.$emit('toggleReply', false);
        this.$emit('setShowReplies', { showReplies: true, commentId: this.commentId });
      }).catch(err => {
        this.setSnackbar({
          text: 'Unable to post a comment.',
          color: 'error'
        });
      })
    },
    createDummyComment(parentId = null) {
      this.interactionMeta.isInteractionAnswered = true;
      const comment = {
        displayTime: this.displayTime,
        id: new Date().getTime(),
        interactionAttemptId: 0,
        interactionId: this.interaction.id,
        name: `${this.user.firstName} ${this.user.lastName}`,
        parentId,
        replies: [],
        self: true,
        showReplies: true,
        teacherId: 0,
        text: this.commentInput,
        archived: "2100-12-01T00:00:00.000Z",
        created: moment(),
        updated: "2100-12-01T00:00:00.000Z",
      }
      this.createDummyDiscussionComment({
        interactionId: this.interactionId,
        comment
      })
      .then(() => {
        this.$refs.tinymce.clear();
        this.$emit('toggleReply', false);
        this.$emit('setShowReplies', { showReplies: true, commentId: this.commentId });
      });
    },
    handleTinymceInput(value) {
      if (
        this.playerState === 'play'
        && !this.isLiveSession
      ) EventBus.$emit('requestAction', 'pause');
      this.commentInput = value;
      this.$set(this.interactionAttempt.responseText, 0, value);
    },
    mouseOver() {
      this.showTooltip = true;
      clearTimeout(this.hoverTimer);
    },
    mouseExit() {
      this.hoverTimer = setTimeout(() => {
        this.showTooltip = false;
      }, 700);
    },
    refreshDiscussionComments() {
      this.setSnackbar({
  				text:'Fetching new comments.',
  	  		color: 'success',
  	  		timeout: 5000,
		  });
		  window.setTimeout(() => {
        // store the old discussion comments
        this.setOldDiscussionComments(this.$store.getters['interactions/discussionComments'](this.interaction.id));
        this.loadDiscussionComments({
          sectionId: this.bulbAttempt.sectionId,
          interactionId: this.interaction.id
        }).then(() => {
          if (this.$store.getters['interactions/discussionComments'](this.interaction.id).length > this.$store.getters['interactions/oldDiscussionComments'].length){
            this.setSnackbar({
    					text:'New comments loaded.',
    		  		color: 'success',
    		  		timeout: 5000,
    				});
          } else {
            let newDiscussion = [];
            let oldDiscussion = [];
            this.$store.getters['interactions/discussionComments'](this.interaction.id).forEach((item) => {
              newDiscussion.push(item.replies.length);
            });
            this.$store.getters['interactions/oldDiscussionComments'].forEach((item) => {
              oldDiscussion.push(item.replies.length);
            });
            oldDiscussion = oldDiscussion.join('');
            newDiscussion = newDiscussion.join('');
            if (oldDiscussion !== newDiscussion) {
              this.setSnackbar({
      					text:'New reply comments loaded.',
      		  		color: 'success',
      		  		timeout: 5000,
    				  });
            } else {
              this.setSnackbar({
      					text:'No new comments found.',
      		  		color: 'success',
      		  		timeout: 5000,
    				  });            
            }
          }
        });
		  }, 2000);
    },
    updateComment() {
      if (this.discussionPreview) {
        this.updateDummyComment(this.commentInput);
        return;
      }
      const comment = {
        id: this.commentId,
        text: this.commentInput,
      }
      this.interactionAttempt.displayTime = this.displayTime;
      this.updateDiscussionComment({interactionId:this.interactionId, comment})
      .then(updated => this.loadDiscussionComments({
          sectionId: this.bulbAttempt.sectionId,
          interactionId: this.interactionId
        })
      ).then(() => {
        this.$refs.tinymce.clear();
        this.$emit('toggleEdit', false);
      }).catch(error => {
        this.setSnackbar({
          text: 'Unable to update comment.',
          color: 'error'
        });
      })
    },
    updateDummyComment(text){
      this.updateDummyDiscussionComment({
        interactionId: this.interactionId,
        commentId: this.commentId,
        text,
      })
      .then(() => {
        this.$refs.tinymce.clear();
        this.$emit('toggleEdit', false);
      });
    },
    expandAllReplyThreads(val) {
      this.$emit('expand-all-reply-threads', val);
    },
  },
  watch: {
    'visible'(){
      if (this.visible) this.commentText = this.startingText;
    }
  },
  mounted() {
    this.enableUpload = !!_.get(this.interaction, 'config.enable_upload', true);
  }
}

</script>


<style type="text/css">
  .discussion-input {
    float:left;
    width: calc(100% - 44px);
  }
</style>
